import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders, isSuperuser, fetchMe } from '../api/auth';
import PageContainer from '../components/PageContainer';

interface UserDomain {
  id: number;
  slug: string;
  name_en: string;
}

interface UserRow {
  id: number;
  username: string;
  is_superuser: boolean;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  domains: UserDomain[];
}

interface DomainOption {
  id: number;
  slug: string;
  name_en: string;
}

const API_BASE = '/api';

const Users: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [domains, setDomains] = useState<DomainOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersResp, domsResp] = await Promise.all([
        axios.get(`${API_BASE}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/admin/data/domains?page_size=200`, { headers: getAuthHeaders() }),
      ]);
      setUsers(usersResp.data);
      setDomains(
        (domsResp.data.items || []).map((d: any) => ({ id: d.id, slug: d.slug, name_en: d.name_en }))
      );
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isSuperuser()) {
    return (
      <PageContainer title="Users & permissions">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm">
          Superuser privileges required to manage users.
        </div>
      </PageContainer>
    );
  }

  const handleDelete = async (u: UserRow) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/users/${u.id}`, { headers: getAuthHeaders() });
      await load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (u: UserRow) => {
    try {
      await axios.patch(
        `${API_BASE}/admin/users/${u.id}`,
        { is_active: !u.is_active },
        { headers: getAuthHeaders() }
      );
      await load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <PageContainer
      title="Users & permissions"
      subtitle="Create administrators and grant them access to specific domains. Superusers have access to everything including chatbot settings and global content."
      actions={<button onClick={() => setShowCreate(true)} className="btn-primary">+ New user</button>}
    >
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-primary-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-primary-50 text-primary-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Username</th>
              <th className="text-left px-4 py-3 font-semibold">Role</th>
              <th className="text-left px-4 py-3 font-semibold">Domains</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">Last login</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-primary-400">Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-primary-400">No users yet.</td></tr>
            )}
            {users.map(u => (
              <tr key={u.id} className="border-t border-primary-100">
                <td className="px-4 py-3 font-medium text-primary-900">{u.username}</td>
                <td className="px-4 py-3">
                  {u.is_superuser ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100">
                      Superuser
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary-50 text-primary-600 border border-primary-200">
                      Editor
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-primary-700">
                  {u.is_superuser ? (
                    <span className="text-primary-400 italic">(all)</span>
                  ) : u.domains.length === 0 ? (
                    <span className="text-primary-400 italic">none</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {u.domains.map(d => (
                        <span key={d.id} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary-100 text-primary-700">
                          {d.name_en}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.is_active ? (
                    <span className="text-emerald-700 text-xs font-medium">Active</span>
                  ) : (
                    <span className="text-primary-400 text-xs">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-primary-500 text-xs">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setEditingUser(u)}
                    className="text-xs font-medium text-primary-600 hover:text-primary-900 mr-3"
                  >Edit</button>
                  <button
                    onClick={() => handleToggleActive(u)}
                    className="text-xs font-medium text-primary-600 hover:text-primary-900 mr-3"
                  >{u.is_active ? 'Deactivate' : 'Activate'}</button>
                  <button
                    onClick={() => handleDelete(u)}
                    className="text-xs font-medium text-red-600 hover:text-red-800"
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <UserFormModal
          domains={domains}
          onClose={() => setShowCreate(false)}
          onSaved={async () => { setShowCreate(false); await load(); }}
        />
      )}
      {editingUser && (
        <UserFormModal
          user={editingUser}
          domains={domains}
          onClose={() => setEditingUser(null)}
          onSaved={async () => {
            setEditingUser(null);
            await load();
            // If the current user edited themselves, refresh the cached profile
            // so the sidebar/permissions reflect any change immediately.
            await fetchMe();
          }}
        />
      )}
    </PageContainer>
  );
};

// -----------------------------------------------------------------------
// Create/edit modal — kept inline because it's used only here
// -----------------------------------------------------------------------
interface UserFormModalProps {
  user?: UserRow;
  domains: DomainOption[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const UserFormModal: React.FC<UserFormModalProps> = ({ user, domains, onClose, onSaved }) => {
  const editing = !!user;
  const [username, setUsername] = useState(user?.username || '');
  const [password, setPassword] = useState('');
  const [isSuperuserChecked, setIsSuperuserChecked] = useState(user?.is_superuser || false);
  const [selectedDomainIds, setSelectedDomainIds] = useState<number[]>(
    user?.domains.map(d => d.id) || []
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleDomain = (id: number) => {
    setSelectedDomainIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      if (editing && user) {
        // PATCH: only send fields the user might have changed.
        const body: any = {
          is_superuser: isSuperuserChecked,
          domain_ids: isSuperuserChecked ? [] : selectedDomainIds,
        };
        if (password) body.password = password;
        await axios.patch(`${API_BASE}/admin/users/${user.id}`, body, { headers: getAuthHeaders() });
      } else {
        if (!password) { setErr('Password required'); setSaving(false); return; }
        const body = {
          username,
          password,
          is_superuser: isSuperuserChecked,
          domain_ids: isSuperuserChecked ? [] : selectedDomainIds,
        };
        await axios.post(`${API_BASE}/admin/users`, body, { headers: getAuthHeaders() });
      }
      await onSaved();
    } catch (e: any) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-primary-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary-900">{editing ? `Edit ${user!.username}` : 'New user'}</h2>
          <button onClick={onClose} className="text-primary-400 hover:text-primary-700 text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-5">
          {err && <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{err}</div>}

          <div>
            <label className="eyebrow block mb-2">Username</label>
            <input
              className="input-base"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              disabled={editing}
            />
            {editing && <p className="text-xs text-primary-400 mt-1">Username cannot be changed.</p>}
          </div>

          <div>
            <label className="eyebrow block mb-2">{editing ? 'New password (leave blank to keep current)' : 'Password'}</label>
            <input
              className="input-base"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required={!editing}
              minLength={6}
              placeholder={editing ? '••••••••' : 'min 6 chars'}
            />
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg bg-primary-50 border border-primary-200">
            <input
              id="su"
              type="checkbox"
              className="mt-0.5"
              checked={isSuperuserChecked}
              onChange={e => setIsSuperuserChecked(e.target.checked)}
            />
            <label htmlFor="su" className="text-sm text-primary-700 cursor-pointer">
              <span className="font-medium">Superuser</span>
              <span className="block text-xs text-primary-500 mt-0.5">Full access to all domains, users, settings, and global content.</span>
            </label>
          </div>

          {!isSuperuserChecked && (
            <div>
              <label className="eyebrow block mb-2">Allowed domains</label>
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {domains.length === 0 && <p className="text-sm text-primary-400 italic">No domains exist yet.</p>}
                {domains.map(d => (
                  <label key={d.id} className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-primary-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedDomainIds.includes(d.id)}
                      onChange={() => toggleDomain(d.id)}
                    />
                    <span className="text-sm text-primary-700">{d.name_en}</span>
                    <span className="text-xs text-primary-400 ml-auto">{d.slug}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-900">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Users;
